import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Home, RotateCcw } from 'lucide-react';

interface AccessDeniedProps {
  /** Target redirect setelah countdown / tombol ditekan. Default dashboard guru. */
  redirectTo?: string;
  /** Pesan opsional yang ditampilkan. */
  message?: string;
  /** Durasi countdown sebelum auto-redirect (detik). Default 5. */
  countdownSeconds?: number;
}

/**
 * Halaman 403 "Akses Ditolak".
 *
 * Menampilkan pesan akses ditolak, lalu setelah beberapa detik otomatis
 * mengarahkan user kembali ke dashboard. User juga bisa menekan tombol
 * "Kembali ke Dashboard" untuk redirect manual.
 */
export const AccessDenied: React.FC<AccessDeniedProps> = ({
  redirectTo = '/portal-guru',
  message = 'Anda tidak memiliki izin untuk mengakses halaman ini.',
  countdownSeconds = 5,
}) => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) {
      navigate(redirectTo, { replace: true });
      return;
    }

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsLeft, navigate, redirectTo]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6f8f6] p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-rose-100 mb-5">
          <AlertCircle className="size-8 text-rose-600" />
        </div>

        <h1 className="text-2xl font-bold text-[#111813] mb-2">Akses Ditolak</h1>
        <p className="text-[#63886f] mb-1">Kode 403 — Forbidden</p>
        <p className="text-sm text-gray-600 mb-6">{message}</p>

        <div className="mb-6 flex items-center justify-center gap-2 text-sm text-gray-500">
          <RotateCcw className="size-4" />
          <span>
            Anda akan dialihkan ke dashboard dalam{' '}
            <span className="font-bold text-[#111813]">{secondsLeft}</span> detik…
          </span>
        </div>

        <button
          onClick={() => navigate(redirectTo, { replace: true })}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#111813] px-4 py-2.5 text-white transition-colors hover:bg-gray-800"
        >
          <Home className="size-4" />
          Kembali ke Dashboard
        </button>
      </div>
    </div>
  );
};

export default AccessDenied;
